import { useEffect, useState, type ReactNode } from 'react';
import FaqPanel from './FaqPanel';
import SettingsMenu from './SettingsMenu';
import type {
  CurriculumTierId,
  KeyboardLayoutId,
  MiniCheckpoint,
  TierCheckpoint,
  UserGoals,
  UserPreferencesPatch,
  UserProfile,
} from '../types';
import { CURRICULUM } from '../data/curriculum';
import { ALL_BADGES, defaultPreferences } from '../lib/storage';
import {
  isBonusEmailEligible,
  isBonusTalEligible,
  isDictationEligible,
  isStoryEligible,
  isStory2Eligible,
  isMetronomeEligible,
  isShadowEligible,
  isWeakSpotsEligible,
  formatWeakSpotKeysLabel,
  isPunctuationPracticeEligible,
  isCustomPracticeEligible,
  isMentalImageryEligible,
} from '../lib/extraRounds';
import { isSkrivelegEligible } from '../lib/skriveleg';
import { isStory3Eligible } from '../lib/story3';
import { isWarmupEligible } from '../lib/warmup';
import { isFlowPracticeEligible } from '../lib/flowPractice';
import { fetchFlowHealth } from '../lib/flowApi';
import { fetchStory2Health } from '../lib/story2Api';
import { fetchSkrivelegHealth } from '../lib/skrivelegApi';
import { fetchStory3Health } from '../lib/story3Api';
import {
  countAvailableUnits,
} from '../lib/dictationUnits';
import DevToolsPanel from './DevToolsPanel';
import { formatKeystrokeCount } from '../lib/activityLog';
import MetronomeBpmPopover from './MetronomeBpmPopover';
import {
  effectiveMetronomeBpmForUser,
  isMetronomeBpmCustom,
  suggestedMetronomeBpmForUser,
} from '../lib/presentationTempo';
import { metronomeSurvivalRecordMs, formatSurvivalMs } from '../lib/metronomeSurvival';
import GoalRoadmap from './GoalRoadmap';
import ProgressModal from './ProgressModal';
import { needsOnboarding } from '../lib/baseline';
import {
  canStartCheckpoint,
  canStartMiniCheckpoint,
  canStartTrin,
  checkpointLabel,
  getActiveTrin,
  getCheckpointAfterTier,
  getMiniCheckpointAfterTrin,
  getRecommendedMiniCheckpoint,
  isCheckpointAccessible,
  isCheckpointVisible,
  isMiniCheckpointAccessible,
  isMiniCheckpointPassed,
  isMiniCheckpointRecommended,
  isTierPlanned,
  isTierUnlocked,
  isTrinAccessible,
  isTrinCompleted,
  tierStatusLabel,
  trinLabel,
  trinLabelFromId,
} from '../lib/curriculum';
import { PROGRESSION } from '../lib/featureFlags';
import {
  formatCheckpointRecordStats,
  formatTrinRecordStats,
  getCheckpointRecord,
} from '../lib/stats';
import {
  isWpmCheckInDue,
  isWpmCheckInEligible,
  latestWpmCheckIn,
  wpmCheckInDelta,
} from '../lib/wpmCheckIn';

interface DashboardProps {
  user: UserProfile;
  onStartLesson: () => void;
  onStartTrin: (trinId: string) => void;
  onStartCheckpoint: (checkpointId: string) => void;
  onStartMiniCheckpoint: (checkpointId: string) => void;
  onStartWarmup: () => void;
  onStartFlowPractice: () => void;
  onStartMetronome: () => void;
  onStartShadow: () => void;
  onStartDictation: () => void;
  onStartStory: () => void;
  onStartStory2: () => void;
  onStartSkriveleg: () => void;
  onStartStory3: () => void;
  onStartWeakSpots: () => void;
  onStartMentalImagery: () => void;
  onStartPunctuationPractice: () => void;
  onStartCustomPractice: () => void;
  onStartBonusTal: () => void;
  onStartBonusEmail: () => void;
  onSwitchUser: () => void;
  onKeyboardLayoutChange: (layout: KeyboardLayoutId) => void;
  onPreferencesChange: (patch: UserPreferencesPatch) => void;
  onGoalsChange: (patch: Partial<UserGoals>) => void;
  onRetestBaseline: (kind: 'transcription' | 'composition') => void;
  onStartWpmCheckIn: () => void;
  onStartOnboarding: () => void;
  onDevJump?: (trinId: string) => void;
  onDevJumpAndStart?: (trinId: string) => void;
  onDevJumpToDictation?: (trinId: string) => void;
}

export default function Dashboard({
  user,
  onStartLesson,
  onStartTrin,
  onStartCheckpoint,
  onStartMiniCheckpoint,
  onStartWarmup,
  onStartFlowPractice,
  onStartMetronome,
  onStartShadow,
  onStartDictation,
  onStartStory,
  onStartStory2,
  onStartSkriveleg,
  onStartStory3,
  onStartWeakSpots,
  onStartMentalImagery,
  onStartPunctuationPractice,
  onStartCustomPractice,
  onStartBonusTal,
  onStartBonusEmail,
  onSwitchUser,
  onKeyboardLayoutChange,
  onPreferencesChange,
  onGoalsChange,
  onRetestBaseline,
  onStartWpmCheckIn,
  onStartOnboarding,
  onDevJump,
  onDevJumpAndStart,
  onDevJumpToDictation,
}: DashboardProps) {
  const activeTrin = getActiveTrin(user);
  const activeTierId = activeTrin.tierId;
  const [expandedTiers, setExpandedTiers] = useState<Set<CurriculumTierId>>(
    () => new Set([activeTierId]),
  );
  const [progressOpen, setProgressOpen] = useState(false);

  useEffect(() => {
    setExpandedTiers(new Set([activeTierId]));
  }, [user.id, activeTierId]);

  const toggleTier = (tierId: CurriculumTierId) => {
    setExpandedTiers((prev) => {
      const next = new Set(prev);
      if (next.has(tierId)) next.delete(tierId);
      else next.add(tierId);
      return next;
    });
  };

  const recent5 = user.recentResults.slice(0, 5);
  const latestCheckIn = latestWpmCheckIn(user);
  const checkInDelta = wpmCheckInDelta(user);
  const checkInEligible = isWpmCheckInEligible(user);
  const checkInDue = isWpmCheckInDue(user);
  const earned = new Set(user.badges.map((b) => b.id));
  const warmupAvailable = isWarmupEligible(user);
  const flowPracticeAvailable = isFlowPracticeEligible(user);
  const metronomeAvailable = isMetronomeEligible(user);
  const shadowAvailable = isShadowEligible(user);
  const dictationAvailable = isDictationEligible(user);
  const storyAvailable = isStoryEligible(user);
  const story2Available = isStory2Eligible(user);
  const skrivelegAvailable = isSkrivelegEligible(user);
  const story3Available = isStory3Eligible(user);
  const [flowApiMode, setFlowApiMode] = useState<'live' | 'mock' | null>(null);
  const [story2ApiMode, setStory2ApiMode] = useState<'live' | 'mock' | null>(
    null,
  );
  const [story3ApiMode, setStory3ApiMode] = useState<'live' | 'mock' | null>(
    null,
  );
  const [skrivelegApiMode, setSkrivelegApiMode] = useState<
    'live' | 'mock' | null
  >(null);

  useEffect(() => {
    if (!flowPracticeAvailable) return;
    void fetchFlowHealth().then(setFlowApiMode);
  }, [flowPracticeAvailable]);
  useEffect(() => {
    if (!story2Available) return;
    void fetchStory2Health().then(setStory2ApiMode);
  }, [story2Available]);
  useEffect(() => {
    if (!skrivelegAvailable) return;
    void fetchSkrivelegHealth().then(setSkrivelegApiMode);
  }, [skrivelegAvailable]);
  useEffect(() => {
    if (!story3Available) return;
    void fetchStory3Health().then(setStory3ApiMode);
  }, [story3Available]);
  const weakSpotsAvailable = isWeakSpotsEligible(user);
  const punctuationPracticeAvailable = isPunctuationPracticeEligible(user);
  const customPracticeAvailable = isCustomPracticeEligible(user);
  const mentalImageryAvailable = isMentalImageryEligible(user);
  const bonusTalAvailable = isBonusTalEligible(user);
  const bonusEmailAvailable = isBonusEmailEligible(user);
  const weakSpotsFocus = formatWeakSpotKeysLabel(user);
  const dictationUnitLength =
    user.preferences?.dictationUnitLength ??
    defaultPreferences().dictationUnitLength ??
    1;
  const dictationUnitsAvailable = dictationAvailable
    ? countAvailableUnits(user, dictationUnitLength)
    : 0;
  const extraAvailable =
    metronomeAvailable ||
    shadowAvailable ||
    dictationAvailable ||
    storyAvailable ||
    story2Available ||
    story3Available ||
    skrivelegAvailable ||
    weakSpotsAvailable ||
    punctuationPracticeAvailable ||
    customPracticeAvailable ||
    mentalImageryAvailable ||
    bonusTalAvailable ||
    bonusEmailAvailable;
  const recommendedMini = getRecommendedMiniCheckpoint(user);
  const showDevTools =
    import.meta.env.DEV &&
    onDevJump !== undefined &&
    onDevJumpAndStart !== undefined &&
    onDevJumpToDictation !== undefined;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="label">Velkommen tilbage</p>
          <h1 className="mt-1 text-3xl font-semibold text-strong">
            {user.name}
          </h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <FaqPanel />
          <SettingsMenu
            keyboardLayout={user.keyboardLayout}
            onKeyboardLayoutChange={onKeyboardLayoutChange}
            preferences={user.preferences ?? defaultPreferences()}
            onPreferencesChange={onPreferencesChange}
            goals={user.goals}
            onGoalsChange={onGoalsChange}
            onRetestBaseline={onRetestBaseline}
          />
          <button type="button" onClick={onSwitchUser} className="btn-secondary">
            Skift bruger
          </button>
        </div>
      </header>

      <section className="grid grid-cols-4 gap-3">
        <StatCard
          label="Aktivt trin"
          value={trinLabel(activeTrin)}
          valueClassName="text-base sm:text-lg"
          action={
            <button
              type="button"
              onClick={onStartLesson}
              disabled={!canStartTrin(activeTrin)}
              className="mt-3 w-full rounded-lg bg-indigo-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-indigo-500/25"
            >
              {canStartTrin(activeTrin)
                ? 'Start lektion'
                : 'Intet trin at starte'}
            </button>
          }
        />
        <StatCard
          label="XP"
          value={String(user.xp)}
          sub={`${formatKeystrokeCount(user.lifetimeStats?.totalKeystrokes ?? 0)} tastetryk i alt`}
        />
        <StatCard
          label="Streak"
          value={`${user.streak.count} ${user.streak.count === 1 ? 'dag' : 'dage'}`}
          sub={user.streak.lastLessonDate ?? 'Ingen aktivitet endnu'}
        />
        <StatCard
          label="Hastighedstest"
          value={latestCheckIn ? `${latestCheckIn.wpm.toFixed(0)} wpm` : '—'}
          sub={
            latestCheckIn
              ? `${(latestCheckIn.accuracy * 100).toFixed(0)}% · ${
                  checkInDelta !== null
                    ? `${checkInDelta >= 0 ? '+' : ''}${checkInDelta.toFixed(0)} siden sidst · `
                    : ''
                }Se udvikling`
              : checkInEligible
                ? 'Tag din første test'
                : 'Efter første gennemførte trin'
          }
          onClick={
            checkInEligible ? () => setProgressOpen(true) : undefined
          }
        />
      </section>

      {needsOnboarding(user) && (
        <section className="surface border border-amber-200/80 p-4 dark:border-amber-500/30">
          <p className="text-sm text-strong">Fuldfør opsætning</p>
          <p className="mt-1 text-xs text-muted">
            Mål dit udgangspunkt og vælg et mål for at få mest ud af dashboardet.
          </p>
          <button
            type="button"
            onClick={onStartOnboarding}
            className="mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
          >
            Fortsæt opsætning →
          </button>
        </section>
      )}

      {user.goals && <GoalRoadmap user={user} />}

      {checkInEligible && (
        <section
          className={`surface flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between ${
            checkInDue
              ? 'border border-indigo-200/80 dark:border-indigo-500/30'
              : ''
          }`}
        >
          <div>
            <h2 className="label">
              {checkInDue ? 'Ugentlig hastighedstest' : 'Hastighedstest'}
            </h2>
            <p className="mt-1 text-sm text-strong">
              {checkInDue
                ? 'Mål din hastighed under ens forhold — kun taster du har gennemført.'
                : latestCheckIn
                  ? `Seneste: ${latestCheckIn.wpm.toFixed(0)} wpm · ${(latestCheckIn.accuracy * 100).toFixed(0)}%`
                  : 'Tag din første hastighedstest.'}
            </p>
            <p className="mt-0.5 text-xs text-faint">
              Ca. 1 min afskrift · sammenlignelig uge for uge
            </p>
          </div>
          <button
            type="button"
            onClick={onStartWpmCheckIn}
            className={`shrink-0 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition ${
              checkInDue
                ? 'bg-indigo-500 hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30'
                : 'bg-zinc-600 hover:bg-zinc-500 dark:bg-zinc-600 dark:hover:bg-zinc-500'
            }`}
          >
            {checkInDue ? 'Start test' : 'Tag test igen'}
          </button>
        </section>
      )}

      {PROGRESSION.miniCheckpoints && recommendedMini && (
        <section className="surface flex flex-col gap-3 border border-indigo-200/80 p-5 dark:border-indigo-500/25 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="label text-indigo-700 dark:text-indigo-300">
              🚪 Anbefalet mini-checkpoint
            </h2>
            <p className="mt-1 text-sm text-strong">{recommendedMini.title}</p>
            <p className="mt-0.5 text-xs text-muted">
              {recommendedMini.description}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onStartMiniCheckpoint(recommendedMini.id)}
            className="shrink-0 rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400"
          >
            Start kontrol
          </button>
        </section>
      )}

      {showDevTools && (
        <DevToolsPanel
          currentTrinId={user.currentTrinId}
          onJump={onDevJump}
          onJumpAndStart={onDevJumpAndStart}
          onJumpToDictation={onDevJumpToDictation}
        />
      )}

      {flowPracticeAvailable && (
        <section className="surface flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="label">Træningsbane</h2>
            <p className="mt-1 text-sm text-strong">
              Skriv så længe du har lyst — dansk eller engelsk.
            </p>
            <p className="mt-0.5 text-xs text-faint">
              Hele alfabetet med naturlig tegnsætning. Stop med Færdig eller
              Escape. Tæller ikke som trin-lektion.
              {flowApiMode === 'live'
                ? ' Nye emner undervejs når serveren er tilkoblet.'
                : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onStartFlowPractice}
            className="shrink-0 rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
          >
            Start træningsbane
          </button>
        </section>
      )}

      {warmupAvailable && (
        <section className="surface flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="label">Daglig varm-op</h2>
            <p className="mt-1 text-sm text-strong">
              60 sek blanding fra alle de niveauer du har gennemført.
            </p>
            <p className="mt-0.5 text-xs text-faint">
              Holder det gamle stof varmt — med tegn og store bogstaver hvis du
              har gennemført niveau 4+. Tæller ikke som en trin-lektion.
            </p>
          </div>
          <button
            type="button"
            onClick={onStartWarmup}
            className="shrink-0 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-400 dark:shadow-lg dark:shadow-emerald-500/30"
          >
            Start varm-op
          </button>
        </section>
      )}

      {extraAvailable && (
        <section className="surface p-5">
          <h2 className="label">Ekstra øvelser</h2>
          <p className="mt-1 text-xs text-faint">
            Variation til de stærke brugere. Tæller ikke som trin-lektion.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {weakSpotsAvailable && (
              <ExtraRoundCard
                title="Svage punkter"
                description={
                  weakSpotsFocus
                    ? `Målrettet træning af ${weakSpotsFocus} — fejl, tøven og svære kombinationer.`
                    : 'Målrettet træning af det du fejler på eller tøver på.'
                }
                onStart={onStartWeakSpots}
              />
            )}
            {punctuationPracticeAvailable && (
              <ExtraRoundCard
                title="Øverunde: Komma og punktum"
                description="Korte sætninger og lister — komma og punktum i rigtig brug."
                onStart={onStartPunctuationPractice}
              />
            )}
            {customPracticeAvailable && (
              <ExtraRoundCard
                title="Vælg selv"
                description="Vælg 1–5 taster på tastaturet og få en blandet øvelse."
                onStart={onStartCustomPractice}
              />
            )}
            {mentalImageryAvailable && (
              <ExtraRoundCard
                title="Imaginær skrivning"
                description="10 korte ord (max 4 bogstaver) · se, blink, diktat. Ro, ingen tidspres."
                onStart={onStartMentalImagery}
              />
            )}
            {storyAvailable && (
              <ExtraRoundCard
                title="Fortælling"
                description="Lyt og skriv prikker og egne ord i en lille historie. Eksperiment."
                badge="Eksperiment"
                onStart={onStartStory}
              />
            )}
            {story2Available && (
              <ExtraRoundCard
                title="Fortælling 2"
                description="Skriv en ny historie på skærmen — find på ord i huller undervejs."
                badge={
                  story2ApiMode === 'mock'
                    ? 'Eksperiment · demo'
                    : 'Eksperiment'
                }
                onStart={onStartStory2}
              />
            )}
            {story3Available && (
              <ExtraRoundCard
                title="Fortælling 3"
                description={
                  (user.preferences?.story3Notebook?.length ?? 0) > 0
                    ? `Notesbog med ${user.preferences!.story3Notebook!.length} gemte tekster — eller start en ny.`
                    : 'Skriv din egen fortælling — gem tekster i notesbogen når du vil.'
                }
                badge={
                  (user.preferences?.story3Notebook?.length ?? 0) > 0
                    ? `Notesbog · ${user.preferences!.story3Notebook!.length}`
                    : story3ApiMode === 'mock'
                      ? 'Eksperiment · demo'
                      : 'Eksperiment'
                }
                onStart={onStartStory3}
              />
            )}
            {skrivelegAvailable && (
              <ExtraRoundCard
                title="Skriveleg"
                description="Chat med AI — gæt Mystery eller overbevis modstandere."
                badge={
                  skrivelegApiMode === 'mock'
                    ? 'Eksperiment · demo'
                    : 'Eksperiment'
                }
                onStart={onStartSkriveleg}
              />
            )}
            {dictationAvailable && (
              <DictationExtraCard
                availableCount={dictationUnitsAvailable}
                onStart={onStartDictation}
              />
            )}
            {metronomeAvailable && (
              <MetronomeExtraCard
                user={user}
                onPreferencesChange={onPreferencesChange}
                onStart={onStartMetronome}
              />
            )}
            {shadowAvailable && (
              <ExtraRoundCard
                title="Skygge-runde"
                description="Teksten ruller af sig selv. Følg med — ingen tid til at tænke."
                onStart={onStartShadow}
              />
            )}
            {bonusTalAvailable && (
              <ExtraRoundCard
                title="Bonus: Tal"
                description="Lær 0–9 på talrækken — til datoer, alder og mængder."
                onStart={onStartBonusTal}
              />
            )}
            {bonusEmailAvailable && (
              <ExtraRoundCard
                title="Bonus: @ og e-mail"
                description="Skriv @ og korte e-mailadresser med Option/Alt Gr."
                onStart={onStartBonusEmail}
              />
            )}
          </div>
        </section>
      )}

      <section className="surface p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="label">Dit forløb</h2>
            <p className="mt-1 text-xs text-faint">
              Din status — hvad du har klaret, og hvad der er aktivt nu.
            </p>
          </div>
          <button
            type="button"
            onClick={onStartLesson}
            disabled={!canStartTrin(activeTrin)}
            className="shrink-0 rounded-xl bg-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-lg dark:shadow-indigo-500/30"
          >
            {!canStartTrin(activeTrin) ? 'Intet trin at starte' : 'Start lektion'}
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {CURRICULUM.map((tier) => {
            const tierOpen = isTierUnlocked(user, tier.id);
            const tierDone = user.completedTierIds.includes(tier.id);
            const planned = isTierPlanned(tier);
            const expanded = expandedTiers.has(tier.id);
            const checkpoint = getCheckpointAfterTier(tier.id);
            const showCheckpoint =
              checkpoint &&
              isCheckpointVisible(user, checkpoint);
            return (
              <div
                key={tier.id}
                className="overflow-hidden rounded-xl ring-1 ring-zinc-200 dark:ring-white/5"
              >
                <button
                  type="button"
                  onClick={() => toggleTier(tier.id)}
                  aria-expanded={expanded}
                  className="flex w-full items-center justify-between gap-3 bg-zinc-50/80 px-4 py-3 text-left transition hover:bg-zinc-100 dark:bg-zinc-900/40 dark:hover:bg-zinc-800/60"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className={`shrink-0 text-faint transition-transform ${expanded ? 'rotate-90' : ''}`}
                      aria-hidden
                    >
                      ›
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-strong">{tier.title}</h3>
                      <p className="truncate text-xs text-muted">
                        {tier.description}
                        {showCheckpoint && !expanded ? ' · Pop-quiz' : ''}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs ring-1 ${
                      planned
                        ? 'bg-zinc-100 text-faint ring-zinc-200 dark:bg-zinc-800 dark:ring-white/10'
                        : tierDone
                          ? 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/30'
                          : tierOpen
                            ? 'bg-indigo-100 text-indigo-800 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-200 dark:ring-indigo-500/30'
                            : 'bg-zinc-100 text-faint ring-zinc-200 dark:bg-zinc-800 dark:ring-white/10'
                    }`}
                  >
                    {tierStatusLabel(tier, {
                      unlocked: tierOpen,
                      completed: tierDone,
                    })}
                  </span>
                </button>

                {expanded && (
                  <div className="border-t border-zinc-200 bg-white p-3 dark:border-white/5 dark:bg-zinc-950/40">
                    {planned ? (
                      <p className="text-sm text-muted">{tier.description}</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {tier.trins.flatMap((t) => {
                          const isCurrent = t.id === user.currentTrinId;
                          const done = isTrinCompleted(user, t.id);
                          const accessible = isTrinAccessible(user, t);
                          const playable = accessible && canStartTrin(t);
                          const recordStats = formatTrinRecordStats(
                            user.trinRecords[t.id],
                            t.id,
                          );
                          const mini =
                            PROGRESSION.miniCheckpoints
                              ? getMiniCheckpointAfterTrin(t.id)
                              : undefined;
                          const rows = [
                            <li key={t.id}>
                              <button
                                type="button"
                                disabled={!playable}
                                onClick={() => playable && onStartTrin(t.id)}
                                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left ring-1 transition ${
                                  isCurrent
                                    ? 'bg-indigo-50 ring-indigo-200 dark:bg-indigo-500/10 dark:ring-indigo-500/30'
                                    : done
                                      ? 'bg-emerald-50 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/30'
                                      : accessible
                                        ? 'bg-white ring-zinc-200 hover:bg-zinc-50 dark:bg-zinc-900/40 dark:ring-white/5 dark:hover:bg-zinc-800/60'
                                        : 'cursor-not-allowed bg-zinc-50 opacity-50 ring-zinc-200 dark:bg-zinc-900/20 dark:ring-white/5'
                                }`}
                              >
                                <div className="flex min-w-0 items-center gap-3">
                                  <span
                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-mono text-sm ${
                                      done
                                        ? 'bg-emerald-500 text-white'
                                        : isCurrent
                                          ? 'bg-indigo-500 text-white'
                                          : 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                                    }`}
                                  >
                                    {t.trinNumber}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="font-medium text-strong">
                                      {t.title}
                                    </p>
                                    <p className="text-xs text-faint">
                                      {trinLabel(t)}
                                    </p>
                                    <p className="text-xs text-muted">
                                      {t.newKeys.length > 0
                                        ? `Ny: ${t.newKeys.map((k) => (k === ' ' ? '␣' : k)).join(' ')} · ${t.rounds.length} runder`
                                        : `Konsolidering · ${t.rounds.length} runder`}
                                    </p>
                                  </div>
                                </div>
                                <div className="ml-3 shrink-0 text-right">
                                  {recordStats && (
                                    <>
                                      <p className="font-mono text-xs text-strong">
                                        {recordStats.primary}
                                      </p>
                                      <p className="text-xs text-faint">
                                        {recordStats.secondary}
                                      </p>
                                    </>
                                  )}
                                  <p className="text-xs text-faint">
                                    {done
                                      ? 'Klaret'
                                      : isCurrent
                                        ? 'Aktiv'
                                        : accessible
                                          ? playable
                                            ? 'Spil'
                                            : ''
                                          : 'Låst'}
                                  </p>
                                </div>
                              </button>
                            </li>,
                          ];
                          if (mini) {
                            rows.push(
                              <li key={mini.id}>
                                <MiniCheckpointCard
                                  checkpoint={mini}
                                  user={user}
                                  onStart={() =>
                                    onStartMiniCheckpoint(mini.id)
                                  }
                                />
                              </li>,
                            );
                          }
                          return rows;
                        })}
                        {showCheckpoint && checkpoint && (
                          <li>
                            <CheckpointCard
                              checkpoint={checkpoint}
                              user={user}
                              onStart={() => onStartCheckpoint(checkpoint.id)}
                            />
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="surface p-5">
        <h2 className="label">Seneste lektioner</h2>
        {recent5.length === 0 ? (
          <p className="mt-3 text-sm text-muted">Ingen lektioner endnu.</p>
        ) : (
          <ul className="mt-3 space-y-1.5 text-sm">
            {recent5.map((r, i) => (
              <li key={i} className="flex justify-between gap-2 text-strong">
                <span className="min-w-0 truncate text-muted">
                  {formatDate(r.startedAt)} · {trinLabelFromId(r.trinId)}
                </span>
                <span className="shrink-0 font-mono">
                  {r.wpm.toFixed(0)} wpm · {(r.accuracy * 100).toFixed(0)}%
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="surface p-5">
        <h2 className="label">Medaljer</h2>
        <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-6">
          {ALL_BADGES.map((b) => (
            <div
              key={b.id}
              className={`rounded-xl p-3 text-center ring-1 ${
                earned.has(b.id)
                  ? 'bg-amber-100 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30'
                  : 'bg-zinc-50 opacity-40 ring-zinc-200 grayscale dark:bg-zinc-900/40 dark:ring-white/5'
              }`}
              title={b.description}
            >
              <p className="mt-1 text-xs font-medium text-strong">{b.name}</p>
            </div>
          ))}
        </div>
      </section>

      {progressOpen && (
        <ProgressModal user={user} onClose={() => setProgressOpen(false)} />
      )}
    </div>
  );
}

function DictationExtraCard({
  availableCount,
  onStart,
}: {
  availableCount: number;
  onStart: () => void;
}) {
  const canStart = availableCount > 0;

  return (
    <button
      type="button"
      aria-disabled={!canStart}
      onClick={() => canStart && onStart()}
      className={`relative flex flex-col gap-1 rounded-xl bg-indigo-50/70 px-4 py-3 text-left ring-1 ring-indigo-200 transition dark:bg-indigo-500/10 dark:ring-indigo-500/30 ${
        canStart
          ? 'hover:bg-indigo-50 dark:hover:bg-indigo-500/15'
          : 'cursor-default opacity-50'
      }`}
    >
      <span className="pr-16 text-sm font-semibold text-strong">Orddiktat</span>
      <span className="text-xs text-muted">
        {canStart
          ? `${availableCount} enheder · 5 pr. session`
          : 'Ingen enheder med dine bogstaver endnu'}
      </span>
    </button>
  );
}

function MetronomeExtraCard({
  user,
  onPreferencesChange,
  onStart,
}: {
  user: UserProfile;
  onPreferencesChange: (patch: UserPreferencesPatch) => void;
  onStart: () => void;
}) {
  const bpm = effectiveMetronomeBpmForUser(user);
  const suggested = suggestedMetronomeBpmForUser(user);
  const survivalRecord = metronomeSurvivalRecordMs(user);

  return (
    <button
      type="button"
      onClick={onStart}
      className="relative flex flex-col gap-1 rounded-xl bg-indigo-50/70 px-4 py-3 text-left ring-1 ring-indigo-200 transition hover:bg-indigo-50 dark:bg-indigo-500/10 dark:ring-indigo-500/30 dark:hover:bg-indigo-500/15"
    >
      <div className="absolute right-2 top-2">
        <MetronomeBpmPopover
          bpm={bpm}
          suggested={suggested}
          custom={isMetronomeBpmCustom(user)}
          onBpmChange={(v) => onPreferencesChange({ metronomeBpm: v })}
          onUseSuggested={() => onPreferencesChange({ metronomeBpm: null })}
        />
      </div>
      <span className="pr-16 text-sm font-semibold text-strong">Rytme-runde</span>
      <span className="text-xs text-muted">
        Skriv én tast pr. tik — klassisk eller overlevelse
        {survivalRecord > 0
          ? ` · rekord ${formatSurvivalMs(survivalRecord)}`
          : ''}
      </span>
    </button>
  );
}

function ExtraRoundCard({
  title,
  description,
  badge,
  onStart,
}: {
  title: string;
  description: string;
  badge?: string;
  onStart: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onStart}
      className="flex flex-col gap-1 rounded-xl bg-indigo-50/70 px-4 py-3 text-left ring-1 ring-indigo-200 transition hover:bg-indigo-50 dark:bg-indigo-500/10 dark:ring-indigo-500/30 dark:hover:bg-indigo-500/15"
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-strong">{title}</span>
        {badge ? (
          <span className="rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-800 ring-1 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-500/30">
            {badge}
          </span>
        ) : null}
      </div>
      <span className="text-xs text-muted">{description}</span>
    </button>
  );
}

function CheckpointCard({
  checkpoint,
  user,
  onStart,
}: {
  checkpoint: TierCheckpoint;
  user: UserProfile;
  onStart: () => void;
}) {
  const accessible = isCheckpointAccessible(user, checkpoint);
  const playable = accessible && canStartCheckpoint(checkpoint);
  const recordStats = formatCheckpointRecordStats(
    getCheckpointRecord(user, checkpoint.id),
  );

  return (
    <button
      type="button"
      disabled={!playable}
      onClick={() => playable && onStart()}
      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left ring-1 transition ${
        playable
          ? 'bg-amber-50/80 ring-amber-200/80 hover:bg-amber-50 dark:bg-amber-500/10 dark:ring-amber-500/25 dark:hover:bg-amber-500/15'
          : 'cursor-not-allowed bg-zinc-50 opacity-50 ring-zinc-200 dark:bg-zinc-900/20 dark:ring-white/5'
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500 text-sm text-white"
          aria-hidden
        >
          ⚡
        </span>
        <div className="min-w-0">
          <p className="font-medium text-strong">{checkpoint.title}</p>
          <p className="text-xs text-faint">
            Pop-quiz · {checkpointLabel(checkpoint)} · 60 sek
          </p>
        </div>
      </div>
      <div className="ml-3 shrink-0 text-right">
        {recordStats && (
          <>
            <p className="font-mono text-xs text-strong">{recordStats.primary}</p>
            <p className="text-xs text-faint">{recordStats.secondary}</p>
          </>
        )}
        <p className="text-xs text-faint">
          {playable ? 'Spil' : 'Låst'}
        </p>
      </div>
    </button>
  );
}

function MiniCheckpointCard({
  checkpoint,
  user,
  onStart,
}: {
  checkpoint: MiniCheckpoint;
  user: UserProfile;
  onStart: () => void;
}) {
  const accessible = isMiniCheckpointAccessible(user, checkpoint);
  const passed = isMiniCheckpointPassed(user, checkpoint.id);
  const recommended = isMiniCheckpointRecommended(user, checkpoint);
  const playable =
    accessible && canStartMiniCheckpoint(checkpoint) && !passed;
  const record = user.miniCheckpointRecords?.[checkpoint.id];
  const secs = Math.round(checkpoint.durationMs / 1000);

  return (
    <button
      type="button"
      disabled={!playable && !passed}
      onClick={() => playable && onStart()}
      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left ring-1 transition ${
        passed
          ? 'bg-emerald-50/80 ring-emerald-200/80 dark:bg-emerald-500/10 dark:ring-emerald-500/25'
          : playable
            ? 'bg-indigo-50/80 ring-indigo-200/80 hover:bg-indigo-50 dark:bg-indigo-500/10 dark:ring-indigo-500/25 dark:hover:bg-indigo-500/15'
            : 'cursor-not-allowed bg-zinc-50 opacity-50 ring-zinc-200 dark:bg-zinc-900/20 dark:ring-white/5'
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-500 text-sm text-white"
          aria-hidden
        >
          🚪
        </span>
        <div className="min-w-0">
          <p className="font-medium text-strong">{checkpoint.title}</p>
          <p className="text-xs text-faint">
            Mini-checkpoint · {secs} sek
            {recommended ? ' · Anbefalet' : ''}
          </p>
        </div>
      </div>
      <div className="ml-3 shrink-0 text-right">
        {record && record.bestWords > 0 && (
          <p className="font-mono text-xs text-strong">
            {record.bestWords} ord
            {record.bestAccuracy !== undefined
              ? ` · ${(record.bestAccuracy * 100).toFixed(0)}%`
              : ''}
          </p>
        )}
        <p className="text-xs text-faint">
          {passed ? 'Bestået' : playable ? 'Start' : 'Låst'}
        </p>
      </div>
    </button>
  );
}

function StatCard({
  label,
  value,
  sub,
  valueClassName,
  onClick,
  action,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClassName?: string;
  onClick?: () => void;
  action?: ReactNode;
}) {
  const inner = (
    <>
      <p className="label">{label}</p>
      <p
        className={`mt-1 font-semibold leading-snug text-strong ${valueClassName ?? 'text-xl'}`}
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 truncate text-xs text-faint">{sub}</p>}
      {action}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="surface w-full p-4 text-left transition hover:ring-2 hover:ring-indigo-200 dark:hover:ring-indigo-500/40"
        aria-label={`${label} — åbn udvikling`}
      >
        {inner}
      </button>
    );
  }

  return <div className="surface p-4">{inner}</div>;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return iso;
  }
}
